import { forwardRef, useId } from 'react';
import cx from 'classnames';
import './RadioCard.css';

/**
 * QHS RadioCard
 * -------------
 * A radio control rendered as a selectable card. Use when each option
 * deserves its own card-sized hit target with a title + supporting
 * description and an optional icon / badge — e.g. SLIT vs SCIT order
 * type, account-tier picker, billing-cycle selector.
 *
 *   <RadioCard
 *     name="category"
 *     value="slit"
 *     checked={cat === 'slit'}
 *     onChange={...}
 *     title="SLIT"
 *     subtitle="Sublingual Immunotherapy — drops administered under the tongue."
 *     badge="SLIT"
 *   />
 *
 * For groups, render in a CSS grid (one column per card on mobile,
 * 2-3 on desktop). The component itself does not own layout; compose
 * with `RadioCardGroup` (default: responsive 1/2 column grid) or your
 * own grid wrapper.
 */
const RadioCard = forwardRef(function RadioCard(
  {
    title,
    subtitle,
    badge,
    icon,
    disabled,
    className,
    id,
    children,
    ...rest
  },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;

  return (
    <label
      htmlFor={inputId}
      className={cx('qhs-radio-card', disabled && 'qhs-radio-card--disabled', className)}
    >
      <input
        ref={ref}
        id={inputId}
        type='radio'
        className='qhs-radio-card__input'
        disabled={disabled}
        {...rest}
      />
      <span className='qhs-radio-card__dot' aria-hidden='true' />
      <span className='qhs-radio-card__body'>
        <span className='qhs-radio-card__head'>
          {badge && <span className='qhs-radio-card__badge'>{badge}</span>}
          {icon && <span className='qhs-radio-card__icon' aria-hidden='true'>{icon}</span>}
          {title && <span className='qhs-radio-card__title'>{title}</span>}
        </span>
        {subtitle && <span className='qhs-radio-card__subtitle'>{subtitle}</span>}
        {children && <span className='qhs-radio-card__extra'>{children}</span>}
      </span>
    </label>
  );
});

/**
 * Convenience grid wrapper. Renders children in a responsive
 * 1-column (mobile) / `columns`-column (desktop) grid with a 12px gap.
 */
const RadioCardGroup = ({ columns = 2, gap = 12, className, children, style, ...rest }) => (
  <div
    className={cx('qhs-radio-card-group', className)}
    style={{ '--qhs-rcg-cols': columns, '--qhs-rcg-gap': `${gap}px`, ...style }}
    role='radiogroup'
    {...rest}
  >
    {children}
  </div>
);

RadioCard.Group = RadioCardGroup;

export { RadioCard, RadioCardGroup };
export default RadioCard;
